// Beispiel für einen einfachen Cookie-Hinweis (z.B. in App.jsx einfügen)
import React, { useState, useEffect } from "react";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";

export function CookieBanner({ open, onClose, onConsentChange, forceShow }) {
  const [cookieSettings, setCookieSettings] = useState({
    necessary: true, // Always active
    functional: false,
    analytical: false,
    performance: false,
    advertising: false
  });

  // Check if user has already given consent
  useEffect(() => {
    const savedSettings = localStorage.getItem('cookieSettings');
    const cookieConsent = localStorage.getItem('cookieConsentGiven');
    
    if (savedSettings && cookieConsent && !forceShow) {
      try {
        const parsedSettings = JSON.parse(savedSettings);
        setCookieSettings(parsedSettings);

        // Update parent component with saved consent
        const hasOptionalCookies = parsedSettings.functional || parsedSettings.analytical || 
                                  parsedSettings.performance || parsedSettings.advertising;
        onConsentChange(parsedSettings.necessary || hasOptionalCookies);
        onClose(); // Don't show popup if consent already given and not forceShow
      } catch (error) {
        console.error('Error loading cookie settings:', error);
        // Do nothing, let the dialog open
      }
    }
    // else: do nothing, let the dialog open
  }, [onConsentChange, onClose, forceShow]);

  // Save cookie preferences to localStorage and set actual cookies
  const saveCookieSettings = (newSettings) => {
    // Save to localStorage
    localStorage.setItem('cookieSettings', JSON.stringify(newSettings));
    localStorage.setItem('cookieConsentGiven', 'true');
    
    // Set cookie consent timestamp
    const expiryDate = new Date();
    expiryDate.setFullYear(expiryDate.getFullYear() + 1); // 1 year expiry
    
    // Set individual cookie permissions
    document.cookie = `cookieConsent=granted; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`;
    document.cookie = `cookieSettings=${JSON.stringify(newSettings)}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`;
    
    // Set specific cookie types based on user preferences
    if (newSettings.functional) {
      document.cookie = `functionalCookies=enabled; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`;
    } else {
      document.cookie = `functionalCookies=disabled; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`;
    }
    
    if (newSettings.analytical) {
      document.cookie = `analyticalCookies=enabled; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`;
      // Here you could initialize analytics (Google Analytics, etc.)
    } else {
      document.cookie = `analyticalCookies=disabled; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`;
    }
    
    if (newSettings.performance) {
      document.cookie = `performanceCookies=enabled; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`;
    } else {
      document.cookie = `performanceCookies=disabled; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`;
    }
    
    if (newSettings.advertising) {
      document.cookie = `advertisingCookies=enabled; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`;
    } else {
      document.cookie = `advertisingCookies=disabled; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`;
    }
  };

  const handleOkClick = () => {
    // Save current settings
    saveCookieSettings(cookieSettings);
    
    // Update parent component
    const hasOptionalCookies = cookieSettings.functional || cookieSettings.analytical || 
                              cookieSettings.performance || cookieSettings.advertising;
    onConsentChange(cookieSettings.necessary || hasOptionalCookies);
    
    // Close popup
    onClose();
  };

  const handleOnlyNecessary = () => {
    // Set only necessary cookies
    const onlyNecessary = {
      necessary: true,
      functional: false,
      analytical: false,
      performance: false,
      advertising: false
    };
    
    // Save settings
    setCookieSettings(onlyNecessary);
    saveCookieSettings(onlyNecessary);
    
    // Update parent component
    onConsentChange(true); // Necessary cookies are always true
    
    // Close popup
    onClose();
  };

  const handleCookieSettingChange = (category) => (event) => {
    const newSettings = {
      ...cookieSettings,
      [category]: event.target.checked
    };
    setCookieSettings(newSettings);
  };

  const handleAcceptAll = (event) => {
    const isChecked = event.target.checked;
    
    if (isChecked) {
      // Accept all cookies
      const allAccepted = {
        necessary: true,
        functional: true,
        analytical: true,
        performance: true,
        advertising: true
      };
      setCookieSettings(allAccepted);
    } else {
      // Reject all optional cookies, keep only necessary
      const onlyNecessary = {
        necessary: true,
        functional: false,
        analytical: false,
        performance: false,
        advertising: false
      };
      setCookieSettings(onlyNecessary);
    }
  };

  // Check if all optional cookies are enabled
  const allOptionalEnabled = cookieSettings.functional && cookieSettings.analytical && 
                           cookieSettings.performance && cookieSettings.advertising;

  return (
    <Dialog 
      open={open} 
      maxWidth="md" 
      fullWidth
      disableEscapeKeyDown
      aria-labelledby="cookie-dialog-title"
    >
      <DialogTitle id="cookie-dialog-title">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="h2" sx={{ textAlign: 'left' }}>
            Cookie-Einstellungen
          </Typography>
          <Button 
            variant="contained" 
            onClick={handleOnlyNecessary}
            size="small"
            sx={{ 
              fontSize: '0.875rem',
            }}
          >
            Nur notwendige
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Paper sx={{ pt: 2, pl: 2, pr: 2, pb: 2 }} elevation={0}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Wir verwenden Cookies, um Nutzern die Navigation zu erleichtern und bestimmte Funktionen auszuführen. 
            Detaillierte Informationen zu allen Cookies der jeweiligen Einwilligungskategorie finden Sie weiter unten.
          </Typography>
          
          <Typography variant="body2" sx={{ mb: 2 }}>
            Cookies der Kategorie „Notwendig" werden in Ihrem Browser gespeichert, da sie für die Grundfunktionen 
            der Website unerlässlich sind.
          </Typography>
          
          <Typography variant="body2" sx={{ mb: 2 }}>
            Wir verwenden auch Cookies von Drittanbietern, um die Nutzung der Website durch Nutzer zu analysieren, 
            Nutzereinstellungen zu speichern und relevante Inhalte und Werbung bereitzustellen. Diese Cookies werden 
            nur mit Ihrer vorherigen Zustimmung in Ihrem Browser gespeichert.
          </Typography>
          
          <Typography variant="body2" sx={{ mb: 3 }}>
            Sie können einige oder alle dieser Cookies aktivieren oder deaktivieren. Dies kann jedoch Ihr 
            Surferlebnis beeinträchtigen.
          </Typography>

          <Divider sx={{ mb: 2 }} />

          {/* Notwendig */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold">Notwendig</Typography>
              <Typography variant="caption" color="text.secondary">Immer aktiv</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
              Notwendige Cookies sind für die Grundfunktionen der Website unerlässlich. Ohne sie funktioniert 
              die Website nicht wie vorgesehen. Diese Cookies speichern keine personenbezogenen Daten.
            </Typography>
          </Box>

          {/* Funktional */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold">Funktional</Typography>
              <Switch
                checked={cookieSettings.functional}
                onChange={handleCookieSettingChange('functional')}
                size="small"
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
              Funktionale Cookies unterstützen bestimmte Funktionen, z. B. das Teilen von Website-Inhalten auf 
              Social-Media-Plattformen, das Sammeln von Feedback und andere Funktionen von Drittanbietern.
            </Typography>
          </Box>

          {/* Analytisch */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold">Analytisch</Typography>
              <Switch
                checked={cookieSettings.analytical}
                onChange={handleCookieSettingChange('analytical')}
                size="small"
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
              Analytische Cookies werden verwendet, um zu verstehen, wie Nutzer mit der Website interagieren. 
              Diese Cookies liefern Informationen zu Besucherzahlen, Absprungraten, Traffic-Quellen usw.
            </Typography>
          </Box>

          {/* Leistung */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold">Leistung</Typography>
              <Switch
                checked={cookieSettings.performance}
                onChange={handleCookieSettingChange('performance')}
                size="small"
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
              Leistungs-Cookies dienen dazu, wichtige Leistungsindikatoren einer Website zu verstehen und zu 
              analysieren und so Besuchern ein besseres Nutzererlebnis zu bieten.
            </Typography>
          </Box>

          {/* Werbungen */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold">Werbungen</Typography>
              <Switch
                checked={cookieSettings.advertising}
                onChange={handleCookieSettingChange('advertising')}
                size="small"
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
              Werbung-Cookies werden verwendet, um Nutzern personalisierte Werbung basierend auf zuvor besuchten 
              Seiten anzuzeigen und die Effektivität von Werbekampagnen zu analysieren.
            </Typography>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: 'block', gap: 2, justifyContent: 'space-between', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allOptionalEnabled}
                  onChange={handleAcceptAll}
                />
              }
              label="Alle akzeptieren"
            />
            <Divider sx={{ mb: 2 }} />
            <Button 
              variant="contained" 
              onClick={handleOkClick}
              color="primary"
            >
              Diese Einstellungen speichern
            </Button>
          </Box>
        </Paper>
      </DialogContent>
    </Dialog>
  );
}